import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SectionHeader from '../components/SectionHeader';
import { fmt, fmtCost } from '../utils/format';

const BANDS = [
  { key: 'under10',  label: '< 10%',   accent: 'red',  test: (gp) => gp < 10 },
  { key: '10to20',   label: '10–20%',  accent: 'gold', test: (gp) => gp >= 10 && gp < 20 },
  { key: '20to30',   label: '20–30%',  accent: 'blue', test: (gp) => gp >= 20 && gp < 30 },
];

const BandCard = ({ label, count, accent, active, onClick }) => (
  <button
    onClick={onClick}
    className={`stat-card stat-card--${accent}`}
    style={{
      cursor: 'pointer',
      textAlign: 'left',
      border: active ? '2px solid var(--white)' : undefined,
      opacity: active === false ? 0.55 : 1,
    }}
  >
    <div className="stat-card__value">{count}</div>
    <div className="stat-card__label">{label}</div>
  </button>
);

const MarginWatch = () => {
  const navigate = useNavigate();
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [bandFilter, setBandFilter] = useState(null); // null = all bands

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc('get_margin_watch_items');
    if (error) {
      console.error('get_margin_watch_items error:', error);
      setItems([]);
    } else {
      setItems(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = {};
    for (const b of BANDS) c[b.key] = items.filter(i => b.test(Number(i.gp_pct))).length;
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    if (!bandFilter) return items;
    const band = BANDS.find(b => b.key === bandFilter);
    return items.filter(i => band.test(Number(i.gp_pct)));
  }, [items, bandFilter]);

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/utilities')}>← Utilities</button>
        <span className="page-nav__title">Margin Watch</span>
        <button className="app-header__btn" onClick={load}>↻ Refresh</button>
      </div>

      <div className="stats-row">
        {BANDS.map(b => (
          <BandCard
            key={b.key}
            label={b.label}
            count={counts[b.key]}
            accent={b.accent}
            active={bandFilter === b.key || bandFilter === null}
            onClick={() => setBandFilter(prev => prev === b.key ? null : b.key)}
          />
        ))}
      </div>

      <section className="section">
        <SectionHeader
          title="Sales Order Items Below Target Margin"
          count={filtered.length}
          onRefresh={load}
          loading={loading}
        />
        <p className="section__desc">
          Items on sales orders currently in Warehouse Transfer with gross profit below 30%,
          based on current Cin7 cost vs the price charged. Click a card above to filter by band, click again to clear.
        </p>

        {loading && <div className="loading-row">Loading…</div>}
        {!loading && !filtered.length && (
          <div className="empty-row">✓ No items below target margin</div>
        )}

        {!loading && filtered.length > 0 && (
          <table className="data-table">
            <thead>
              <tr>
                <th>Order Ref</th>
                <th>Item Code</th>
                <th>Item Name</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Cost</th>
                <th>GP %</th>
                <th>GP $</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, i) => (
                <tr key={`${row.order_ref}-${row.item_code}-${i}`}>
                  <td><span className="code-badge code-badge--sm">{row.order_ref}</span></td>
                  <td>{row.item_code}</td>
                  <td>{row.item_name}</td>
                  <td>{fmt(row.item_qty)}</td>
                  <td>{fmtCost(row.effective_price)}</td>
                  <td>{fmtCost(row.cost_nzd)}</td>
                  <td style={{ fontWeight: 700, color: row.gp_pct < 10 ? 'var(--red-light, #f87171)' : 'var(--gold, #facc15)' }}>
                    {fmt(row.gp_pct)}%
                  </td>
                  <td>{fmtCost(row.gp_dollars)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
};

export default MarginWatch;
